import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { adminPushApi, type AdminPushRecipient } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

function recipientLabel(recipient: AdminPushRecipient): string {
  if (recipient.displayName?.trim()) return recipient.displayName.trim();
  if (recipient.name?.trim()) return recipient.name.trim();
  if (recipient.email?.trim()) return recipient.email.trim();
  return recipient.userId;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function SendPushPanel() {
  const [recipients, setRecipients] = React.useState<AdminPushRecipient[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [filter, setFilter] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [url, setUrl] = React.useState('/');
  const [classificationTest, setClassificationTest] = React.useState(false);
  const [merchant, setMerchant] = React.useState('Test merchant');
  const [budgetName, setBudgetName] = React.useState('Test budget');
  const [transactionId, setTransactionId] = React.useState('');

  const loadRecipients = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await adminPushApi.listRecipients();
      setRecipients(list);
      setSelected((prev) => {
        const valid = new Set(list.map((r) => r.userId));
        return new Set([...prev].filter((id) => valid.has(id)));
      });
    } catch {
      toast.error('Failed to load push recipients');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadRecipients();
  }, [loadRecipients]);

  const filtered = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter((r) => {
      const haystack = [r.displayName, r.name, r.email, r.userId]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [recipients, filter]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((r) => selected.has(r.userId));

  const toggleUser = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const r of filtered) next.delete(r.userId);
      } else {
        for (const r of filtered) next.add(r.userId);
      }
      return next;
    });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selected.size === 0) {
      toast.error('Select at least one user');
      return;
    }

    if (classificationTest) {
      const txId = transactionId.trim();
      if (!UUID_RE.test(txId)) {
        toast.error('Enter a valid transaction UUID for Yes/No deep links');
        return;
      }
      setSending(true);
      try {
        const result = await adminPushApi.sendClassificationTest({
          userIds: [...selected],
          transactionId: txId,
          merchant: merchant.trim() || undefined,
          budgetName: budgetName.trim() || undefined,
        });
        toast.success(
          `Classification test sent to ${result.requestedUserCount} user${result.requestedUserCount === 1 ? '' : 's'} (${result.subscriptionCount} device${result.subscriptionCount === 1 ? '' : 's'})`
        );
      } catch {
        toast.error('Failed to send classification test push');
      } finally {
        setSending(false);
      }
      return;
    }

    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }

    setSending(true);
    try {
      const result = await adminPushApi.send({
        userIds: [...selected],
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || '/',
      });
      toast.success(
        `Sent to ${result.requestedUserCount} user${result.requestedUserCount === 1 ? '' : 's'} (${result.subscriptionCount} device${result.subscriptionCount === 1 ? '' : 's'})`
      );
    } catch {
      toast.error('Failed to send push notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send custom push</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSend} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Broadcast a custom message to users who already enabled notifications in Settings
            (installed PWA). Enabling push on a device is not an admin action.
          </p>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={classificationTest}
              onChange={(e) => setClassificationTest(e.target.checked)}
            />
            <span className="min-w-0 flex-1 text-sm">
              <span className="block font-medium text-foreground">
                Classification Yes/No test
              </span>
              <span className="block text-muted-foreground">
                Sends the same auto-classify confirmation payload (Yes/No actions). Use a real
                transaction ID the recipient can edit so feedback works.
              </span>
            </span>
          </label>

          {classificationTest ? (
            <>
              <Input
                label="Transaction ID"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                maxLength={36}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                required
                helperText="Opens /transactions/{id}?classification=yes|no"
              />
              <Input
                label="Merchant (optional)"
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                maxLength={200}
                placeholder="Test merchant"
              />
              <Input
                label="Budget name (optional)"
                value={budgetName}
                onChange={(e) => setBudgetName(e.target.value)}
                maxLength={100}
                placeholder="Test budget"
              />
            </>
          ) : (
            <>
              <Input
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
                placeholder="Notification title"
                required
              />

              <div className="w-full">
                <label
                  htmlFor="admin-push-body"
                  className="block text-sm font-medium text-foreground mb-1.5"
                >
                  Message
                </label>
                <textarea
                  id="admin-push-body"
                  className="flex min-h-[96px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={500}
                  placeholder="Custom message"
                  required
                />
              </div>

              <Input
                label="Open URL (optional)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                maxLength={500}
                placeholder="/"
                helperText="Path opened when the notification is tapped"
              />
            </>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-foreground">
                Recipients
                {selected.size > 0 ? ` (${selected.size} selected)` : ''}
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => void loadRecipients()}>
                  Refresh
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleAllFiltered}
                  disabled={filtered.length === 0}
                >
                  {allFilteredSelected ? 'Clear filtered' : 'Select filtered'}
                </Button>
              </div>
            </div>

            <Input
              placeholder="Filter by name or email"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />

            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {recipients.length === 0
                  ? 'No users with active push subscriptions.'
                  : 'No recipients match this filter.'}
              </p>
            ) : (
              <ul className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                {filtered.map((recipient) => {
                  const checked = selected.has(recipient.userId);
                  return (
                    <li key={recipient.userId}>
                      <label
                        className={cn(
                          'flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 text-sm',
                          checked ? 'bg-muted' : 'hover:bg-muted/60'
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={checked}
                          onChange={() => toggleUser(recipient.userId)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium text-foreground">
                            {recipientLabel(recipient)}
                          </span>
                          {recipient.email && recipient.email !== recipientLabel(recipient) && (
                            <span className="block truncate text-muted-foreground">
                              {recipient.email}
                            </span>
                          )}
                          <span className="block text-xs text-muted-foreground">
                            {recipient.subscriptionCount} device
                            {recipient.subscriptionCount === 1 ? '' : 's'}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <Button type="submit" loading={sending} disabled={sending || selected.size === 0}>
            {classificationTest ? 'Send Yes/No test' : 'Send notification'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
